'use client';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LANGUAGE,
  normalizeLanguage,
} from 'features/language/languageConfig';

interface SidebarProps {
  size: string;
  children: ReactNode;
  sidebar: boolean;
  type: 'primary' | 'secondary' | 'third';
  sidebarWidth: number;
  setSidebarWidth: React.Dispatch<React.SetStateAction<number>>;
  className?: string;
  id?: string;
}

const Sidebar: FC<SidebarProps> = ({
  size,
  children,
  sidebar,
  type,
  sidebarWidth,
  setSidebarWidth,
  className,
  id,
}) => {
  const [styles, setStyles] = useState<React.CSSProperties>({});
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  // Direction follows i18next, the store that decides the rendered text, so the
  // sidebar cannot mirror while the copy stays left-to-right.
  const { i18n } = useTranslation(undefined, { useSuspense: false });
  const language = normalizeLanguage(i18n.language) ?? DEFAULT_LANGUAGE;

  useEffect(() => {
    if (sidebarRef.current && type === 'primary') {
      setSidebarWidth(sidebarRef.current.offsetWidth);
    }
  }, [type, setSidebarWidth]);

  useEffect(() => {
    const isRTL = language === 'ar';

    let sidebarStyles: React.CSSProperties;

    if (type === 'primary') {
      sidebarStyles = {
        transform: sidebar
          ? 'translateX(0px)'
          : isRTL
            ? 'translateX(-100%)'
            : 'translateX(100%)',
        // Drawer band (500s): above the header (z-100) and the drawer overlay (z-500), below
        // dialogs/media (z-1000+). third > secondary > primary: below 768px the deeper drawers
        // stop being offset sideways and land ON TOP of the primary, so the most recently
        // opened one has to paint last or it is buried. Above 768px they sit side by side and
        // the order only decides which box-shadow falls on the seam.
        zIndex: 501,
        transition: 'transform 0.3s ease-in-out',
      };
    } else if (type === 'secondary') {
      const direction = isRTL ? '' : '-';
      const offset = sidebarWidth || 432;
      sidebarStyles = {
        transform: sidebar
          ? `translateX(${direction}${isMobile ? 0 : offset}px)`
          : isRTL
            ? 'translateX(-100%)'
            : 'translateX(100%)',
        zIndex: 502,
        transition: 'transform 0.3s ease-in-out',
      };
    } else {
      const direction = isRTL ? '' : '-';
      const offset = sidebarWidth || 432;
      sidebarStyles = {
        transform: sidebar
          ? `translateX(${direction}${isMobile ? 0 : offset * 2}px)`
          : isRTL
            ? 'translateX(-100%)'
            : 'translateX(100%)',
        zIndex: 503,
        transition: 'transform 0.3s ease-in-out',
      };
    }

    setStyles(sidebarStyles);
  }, [sidebar, sidebarWidth, type, language, isMobile]);

  return (
    <div
      id={id}
      ref={sidebarRef}
      className={`minthr-sidebar minthr-flex minthr-flex-column minthr-sidebar-${size} ${className} ${sidebar ? 'active' : ''}`}
      style={styles}
    >
      {children}
    </div>
  );
};

export default Sidebar;
